'use client';

import {
  createContext,
  useCallback,
  useContext,
  useMemo,
  useReducer,
  useRef,
  useState,
  type ReactNode,
} from 'react';
import type { SeatData } from '@/components/grid';
import type { V2GridRewardEvent } from '@/lib/types/v2';
import type { TileAnimState } from '@/features/lobby/board-canvas/TileEffectsOverlay';
import type { PlinthMaterialId } from '@/features/lobby/theming/plinths/types';
import {
  buildTutorialSeats,
  TUTORIAL_FLOOR_INDEX,
  TUTORIAL_VIEWER,
  TUTORIAL_BOARD_ID,
} from './tutorial-board';
import {
  TUTORIAL_STEPS,
  type TutorialStepDefinition,
  type TutorialStepId,
} from './tutorial-script';

/** Plinth material the viewer's tile should regrow with after a buyout in
 *  the tutorial. Hard-coded to obsidian so the post-buyout slab paints
 *  the dramatic black material described in the script copy. */
export const TUTORIAL_PLAYER_PLINTH: PlinthMaterialId = 'obsidian';

/** Imperative bridge the tutorial uses to push tile animation states
 *  into `LobbyClient`'s production `tileAnimStates` map. The tutorial
 *  re-uses the production buyout choreography rather than rolling its
 *  own — `LobbyClient` registers the bridge on mount via
 *  `useRegisterTutorialTileAnim` and clears it on unmount. */
export type TutorialTileAnimBridge = (
  seatIndex: number,
  state: TileAnimState,
) => void;

export type TutorialBranch = 'defend' | 'leave' | null;
export type TutorialDrawerKind = 'buyout' | 'manage' | 'claim' | null;

export interface TutorialDrawer {
  kind: TutorialDrawerKind;
  seatIndex: number | null;
}

interface TutorialState {
  stepId: TutorialStepId;
  seats: SeatData[];
  drawer: TutorialDrawer;
  highlightTarget: string | null;
  /** When true, the tutorial overlay paints a dim mask outside the highlighted cut-out. */
  dim: boolean;
  /** Step 12 branch chosen by the user (defend or leave). */
  branch: TutorialBranch;
  /** Pre-filled price (in token units, NOT bigint) the buyout drawer should snap to during step 7. */
  prefilledBuyoutPrice: number | null;
  /** Live stream of reward events the rewards panel surfaces during the tutorial. */
  rewardEvents: V2GridRewardEvent[];
  /** Cumulative claimable USD the rewards pill / sheet should show. */
  claimableUsd: number;
  claimableCount: number;
  /** When true, the lobby HUD (yield / mytiles / rewards / chat) collapses
   *  off-screen so the user's eye stays on the board during dramatic
   *  beats. Currently used to hide every panel during the
   *  `competitor-buyouts` wave so the 3 crumbles read against an
   *  uncluttered board. `LobbyClient` snapshots its `visiblePanels`
   *  state when this flips to `true` and restores from the snapshot
   *  when it flips back to `false` — see the effect that watches
   *  `tutorial?.state.hudPanelsHidden`. */
  hudPanelsHidden: boolean;
}

type TutorialAction =
  | { type: 'goto'; stepId: TutorialStepId }
  | { type: 'set-seats'; seats: SeatData[] }
  | { type: 'mutate-seats'; updater: (prev: SeatData[]) => SeatData[] }
  | { type: 'open-drawer'; drawer: TutorialDrawer }
  | { type: 'close-drawer' }
  | { type: 'set-highlight'; target: string | null; dim?: boolean }
  | { type: 'set-branch'; branch: TutorialBranch }
  | { type: 'set-prefilled-price'; price: number | null }
  | { type: 'push-reward'; event: V2GridRewardEvent; usd: number }
  | { type: 'reset-rewards' }
  | { type: 'claim-rewards' }
  | { type: 'set-hud-hidden'; hidden: boolean };

function reducer(state: TutorialState, action: TutorialAction): TutorialState {
  switch (action.type) {
    case 'goto':
      return { ...state, stepId: action.stepId };
    case 'set-seats':
      return { ...state, seats: action.seats };
    case 'mutate-seats':
      return { ...state, seats: action.updater(state.seats) };
    case 'open-drawer':
      return { ...state, drawer: action.drawer };
    case 'close-drawer':
      return { ...state, drawer: { kind: null, seatIndex: null } };
    case 'set-highlight':
      return {
        ...state,
        highlightTarget: action.target,
        dim: action.dim ?? state.dim,
      };
    case 'set-branch':
      return { ...state, branch: action.branch };
    case 'set-prefilled-price':
      return { ...state, prefilledBuyoutPrice: action.price };
    case 'push-reward':
      return {
        ...state,
        rewardEvents: [action.event, ...state.rewardEvents].slice(0, 60),
        claimableUsd: state.claimableUsd + action.usd,
        claimableCount: state.claimableCount + 1,
      };
    case 'reset-rewards':
      return { ...state, rewardEvents: [], claimableUsd: 0, claimableCount: 0 };
    case 'claim-rewards':
      return { ...state, claimableUsd: 0, claimableCount: 0 };
    case 'set-hud-hidden':
      // Bail out when the value is unchanged so React 18 short-
      // circuits the dispatch and skips the re-render. The cleanup
      // in `TutorialOverlay` defensively calls
      // `setHudPanelsHidden(false)` on every step exit (most steps
      // never set it true) — without this guard each of those
      // would trigger a no-op re-render.
      if (state.hudPanelsHidden === action.hidden) return state;
      return { ...state, hudPanelsHidden: action.hidden };
    default:
      return state;
  }
}

/**
 * First step the tutorial should land on for the current viewport.
 *
 * Mobile (≤768 px) skips `view-pick` entirely — the 3D cinematic
 * camera doesn't read on a phone and the picker just adds a click the
 * user has to make before reaching the actual content. On mobile we
 * land straight on `intro-board` and the overlay's mount effect pins
 * the renderer to Lite 2D so the lobby canvas matches the picker
 * decision we made on the user's behalf.
 *
 * `<AccessGate>` only mounts the tutorial subtree client-side (it
 * paints a black bed during SSR and resolves on its own
 * `useEffect`), so reading `window.matchMedia` here is safe — there's
 * no SSR pass to mismatch with. On the SSR fallback path (e.g. if
 * `<AccessGate>` is ever removed) we default to `view-pick` so the
 * server-rendered HTML stays deterministic.
 */
function getInitialStepId(): TutorialStepId {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') {
    return 'view-pick';
  }
  return window.matchMedia('(max-width: 768px)').matches ? 'intro-board' : 'view-pick';
}

function initialState(): TutorialState {
  return {
    stepId: getInitialStepId(),
    seats: buildTutorialSeats(),
    drawer: { kind: null, seatIndex: null },
    highlightTarget: null,
    dim: false,
    branch: null,
    prefilledBuyoutPrice: null,
    rewardEvents: [],
    claimableUsd: 0,
    claimableCount: 0,
    hudPanelsHidden: false,
  };
}

export interface TutorialContextValue {
  isActive: true;
  /** Stable id used for the synthetic board entry rendered while the tutorial is up. */
  boardId: typeof TUTORIAL_BOARD_ID;
  state: TutorialState;
  step: TutorialStepDefinition;
  /** Index of the viewer's owned seat on the tutorial board (set after the buyout step). */
  viewerSeatIndex: number;
  /** True once the buyout has completed so own-tile chrome (3D blue ring, mytiles row) lights up. */
  viewerOwnsSeat: boolean;
  /** Plinth material the viewer's regrown slab should paint as. The
   *  tutorial overrides the user's persisted preference so the post-
   *  buyout reveal lands on the dramatic obsidian look the script
   *  promises ("comes in with the user's avatar on an obsidian player
   *  plinth"). Production code keeps reading the user's pick. */
  playerPlinthOverride: PlinthMaterialId;
  /** Imperative API consumed by the overlay + scripted handlers. */
  goto: (stepId: TutorialStepId) => void;
  next: () => void;
  setSeats: (seats: SeatData[]) => void;
  mutateSeats: (updater: (prev: SeatData[]) => SeatData[]) => void;
  /** Synchronous read of the latest seats array. Use this from
   *  setTimeout / event handlers when you need a fresh snapshot —
   *  `mutateSeats(noop)` does NOT eagerly invoke the updater on
   *  React 18's `useReducer` path, so the no-op-mutation pattern
   *  reads a stale value. The ref is updated on every commit so
   *  this always returns what the canvas is currently rendering. */
  getSeats: () => SeatData[];
  openDrawer: (drawer: TutorialDrawer) => void;
  closeDrawer: () => void;
  setHighlight: (target: string | null, dim?: boolean) => void;
  setBranch: (branch: TutorialBranch) => void;
  setPrefilledPrice: (price: number | null) => void;
  pushReward: (event: V2GridRewardEvent, usd: number) => void;
  resetRewards: () => void;
  claimRewards: () => void;
  /** Hide / restore the lobby HUD panels. Used to clear the screen
   *  during the `competitor-buyouts` wave so the 3 crumbles read
   *  against an uncluttered board, then restore the user's
   *  pre-wave panel layout when the wave finishes. */
  setHudPanelsHidden: (hidden: boolean) => void;
  /** Restart the tutorial from the very first step. */
  restart: () => void;
  /** Register the LobbyClient bridge so script side-effects can push
   *  animation states straight into the production tileAnimStates map.
   *  Returns an unsubscribe function. */
  registerTileAnimBridge: (bridge: TutorialTileAnimBridge) => () => void;
  /** Fire a tile animation through the registered bridge. No-op when no
   *  bridge has been registered (LobbyClient still mounting). */
  triggerTileAnim: TutorialTileAnimBridge;
}

const TutorialContext = createContext<TutorialContextValue | null>(null);

interface TutorialProviderProps {
  children: ReactNode;
}

export function TutorialProvider({ children }: TutorialProviderProps) {
  const [state, dispatch] = useReducer(reducer, undefined, initialState);
  // Initial seats key — used so `restart()` re-roles avatars without
  // remounting the whole subtree.
  const [bumpKey, setBumpKey] = useState(0);

  // Live mirror of `state.seats` for synchronous reads. React 18's
  // `useReducer` does NOT eagerly invoke the reducer on dispatch
  // (only `useState`'s `dispatchSetState` does), so the previous
  // pattern of `mutateSeats((prev) => { snapshot = prev[i]; return
  // prev; })` was reading `undefined` from inside setTimeout callbacks
  // — the buyout animation handlers in `TutorialOverlay` were guarded
  // on `if (snapshot)` and silently bailing, which is what the user
  // saw as "no buyout animation" on `competitor-buyouts` and
  // `path-leave`. Updating the ref on every render guarantees
  // `getSeats()` always returns the latest committed seats.
  const seatsRef = useRef<SeatData[]>(state.seats);
  seatsRef.current = state.seats;

  const goto = useCallback((stepId: TutorialStepId) => {
    dispatch({ type: 'goto', stepId });
  }, []);

  const setSeats = useCallback((seats: SeatData[]) => {
    dispatch({ type: 'set-seats', seats });
  }, []);

  const mutateSeats = useCallback(
    (updater: (prev: SeatData[]) => SeatData[]) => {
      dispatch({ type: 'mutate-seats', updater });
    },
    [],
  );

  const getSeats = useCallback(() => seatsRef.current, []);

  const openDrawer = useCallback((drawer: TutorialDrawer) => {
    dispatch({ type: 'open-drawer', drawer });
  }, []);

  const closeDrawer = useCallback(() => {
    dispatch({ type: 'close-drawer' });
  }, []);

  const setHighlight = useCallback((target: string | null, dim?: boolean) => {
    dispatch({ type: 'set-highlight', target, dim });
  }, []);

  const setBranch = useCallback((branch: TutorialBranch) => {
    dispatch({ type: 'set-branch', branch });
  }, []);

  const setPrefilledPrice = useCallback((price: number | null) => {
    dispatch({ type: 'set-prefilled-price', price });
  }, []);

  const pushReward = useCallback((event: V2GridRewardEvent, usd: number) => {
    dispatch({ type: 'push-reward', event, usd });
  }, []);

  const resetRewards = useCallback(() => {
    dispatch({ type: 'reset-rewards' });
  }, []);

  const claimRewards = useCallback(() => {
    dispatch({ type: 'claim-rewards' });
  }, []);

  const setHudPanelsHidden = useCallback((hidden: boolean) => {
    dispatch({ type: 'set-hud-hidden', hidden });
  }, []);

  const restart = useCallback(() => {
    setBumpKey((k) => k + 1);
    dispatch({ type: 'set-seats', seats: buildTutorialSeats() });
    dispatch({ type: 'close-drawer' });
    dispatch({ type: 'set-highlight', target: null, dim: false });
    dispatch({ type: 'set-branch', branch: null });
    dispatch({ type: 'set-prefilled-price', price: null });
    dispatch({ type: 'reset-rewards' });
    dispatch({ type: 'set-hud-hidden', hidden: false });
    // Replays from the outro screen should land on the same first
    // step the initial mount picked — `view-pick` on desktop,
    // `intro-board` on mobile (we don't show the 3D vs 2D picker on a
    // phone-sized viewport).
    dispatch({ type: 'goto', stepId: getInitialStepId() });
  }, []);

  const stepsRef = useRef(TUTORIAL_STEPS);
  const step = useMemo(() => {
    return stepsRef.current.find((s) => s.id === state.stepId) ?? stepsRef.current[0];
  }, [state.stepId]);

  // Tile-anim bridge — set by LobbyClient on mount via
  // `registerTileAnimBridge`. `tileAnimBridgeRef` keeps the live
  // function pointer so `triggerTileAnim` is stable across renders;
  // callers wire it into useEffect deps without thrashing.
  const tileAnimBridgeRef = useRef<TutorialTileAnimBridge | null>(null);
  const registerTileAnimBridge = useCallback(
    (bridge: TutorialTileAnimBridge) => {
      tileAnimBridgeRef.current = bridge;
      return () => {
        if (tileAnimBridgeRef.current === bridge) {
          tileAnimBridgeRef.current = null;
        }
      };
    },
    [],
  );
  const triggerTileAnim = useCallback<TutorialTileAnimBridge>(
    (seatIndex, animState) => {
      const bridge = tileAnimBridgeRef.current;
      if (!bridge) return;
      bridge(seatIndex, animState);
    },
    [],
  );

  const next = useCallback(() => {
    const idx = stepsRef.current.findIndex((s) => s.id === state.stepId);
    if (idx < 0 || idx === stepsRef.current.length - 1) return;
    const nextStep = stepsRef.current[idx + 1];
    dispatch({ type: 'goto', stepId: nextStep.id });
  }, [state.stepId]);

  const viewerOwnsSeat = useMemo(() => {
    return state.seats.some((s) => s.isOwn && s.holder === TUTORIAL_VIEWER.address);
  }, [state.seats]);

  const viewerSeatIndex = useMemo(() => {
    const idx = state.seats.findIndex(
      (s) => s.isOwn && s.holder === TUTORIAL_VIEWER.address,
    );
    return idx >= 0 ? idx : TUTORIAL_FLOOR_INDEX;
  }, [state.seats]);

  const value = useMemo<TutorialContextValue>(
    () => ({
      isActive: true,
      boardId: TUTORIAL_BOARD_ID,
      state,
      step,
      viewerSeatIndex,
      viewerOwnsSeat,
      playerPlinthOverride: TUTORIAL_PLAYER_PLINTH,
      goto,
      next,
      setSeats,
      mutateSeats,
      getSeats,
      openDrawer,
      closeDrawer,
      setHighlight,
      setBranch,
      setPrefilledPrice,
      pushReward,
      resetRewards,
      claimRewards,
      setHudPanelsHidden,
      restart,
      registerTileAnimBridge,
      triggerTileAnim,
    }),
    [
      state,
      step,
      viewerSeatIndex,
      viewerOwnsSeat,
      goto,
      next,
      setSeats,
      mutateSeats,
      getSeats,
      openDrawer,
      closeDrawer,
      setHighlight,
      setBranch,
      setPrefilledPrice,
      pushReward,
      resetRewards,
      claimRewards,
      setHudPanelsHidden,
      restart,
      registerTileAnimBridge,
      triggerTileAnim,
    ],
  );

  return (
    <TutorialContext.Provider key={bumpKey} value={value}>
      {children}
    </TutorialContext.Provider>
  );
}

export function useTutorial(): TutorialContextValue {
  const ctx = useContext(TutorialContext);
  if (!ctx) {
    throw new Error('useTutorial must be used within a TutorialProvider');
  }
  return ctx;
}

export function useTutorialOptional(): TutorialContextValue | null {
  return useContext(TutorialContext);
}
